import useBadgeSession, { isBadgeSessionCurrent } from './useBadgeSession';
import { useMemberEntry } from '../members/memberMemory';
import { runBadgeCollectionTask, publishBadgeCollection } from './collectionTasks';
import { refreshBenefitQueries } from '../sponsorship/benefitQueries';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FiChevronLeft, FiChevronRight, FiCheck } from 'react-icons/fi';
import PageLayout from '../../components/layout/PageLayout';
import { useUserStore } from '../../stores/userStore';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import api from '../../api/api';
import useBadges, { badgeKey } from './useBadges';
import { words, unwrapCollection } from './model';
import {
  localizeBadge,
  localizeBadgeCategory,
  localizeSponsorshipStage,
} from './generated/localization';
import { sponsorshipMarkPreview } from './generated/preview';
import { BadgeIcon, SupportMark } from './BadgeIcon';
import useBadgeStyle from './useBadgeStyle';
import useCelebrations from './useCelebrations';
import './badges.css';
import { BadgeSkeleton, BadgeError } from './States';
export default function BadgePage() {
  const session = useBadgeSession(),
    location = useLocation();
  const user = useUserStore((s) => s.user),
    { nickname } = useParams();
  return (
    <BadgeCollection
      key={`${session}:${nickname ?? 'mine'}:${location.key}`}
      session={session}
      user={user}
      nickname={nickname}
    />
  );
}
function BadgeCollection({ user, nickname, session }) {
  const { entry, returning } = useMemberEntry('badge-visit60');
  const restoredVisit = useRef(returning && entry.visited);
  useEffect(() => {
    entry.visited = true;
  }, [entry]);
  const query = useBadges(nickname),
    { locale } = useWebLocale(),
    t = words(locale),
    style = useBadgeStyle(),
    navigate = useNavigate(),
    client = useQueryClient(),
    [params] = useSearchParams(),
    root = useRef(null),
    alive = useRef(true),
    controller = useRef(),
    lock = useRef(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const d = query.data,
    own = !nickname && !!user?.id,
    visible = useCelebrations(
      d,
      own && !d?.preview,
      params.get('focusBadgeKey'),
      root,
      restoredVisit.current,
    );
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      controller.current?.abort();
    };
  }, []);
  const select = async (type, badge) => {
    if (
      !own ||
      !d ||
      d.preview ||
      lock.current ||
      useUserStore.getState().user?.id !== user.id ||
      (type === 'ACTIVITY' && !badge?.unlockedAt) ||
      (type === 'SPONSORSHIP' && !d.sponsorshipMark?.isSupporter)
    )
      return;
    lock.current = true;
    setBusy(true);
    setError('');
    const c = new AbortController();
    controller.current = c;
    const credential = localStorage.getItem('POSTPOP_AUTH_TOKEN');
    const current = () =>
      alive.current &&
      isBadgeSessionCurrent(session) &&
      !c.signal.aborted &&
      useUserStore.getState().user?.id === user.id &&
      localStorage.getItem('POSTPOP_AUTH_TOKEN') === credential;
    try {
      await runBadgeCollectionTask(client, user.id, async () => {
        if (!current()) return;
        const next = unwrapCollection(
          await api.putAuth(
            '/me/badges/profile-mark',
            { type, badgeKey: type === 'ACTIVITY' ? badge.key : null },
            { signal: c.signal, suppressGlobalError: true },
          ),
        );
        if (!current()) return;
        await client.cancelQueries({ queryKey: badgeKey(user.id), exact: true });
        if (!current()) return;
        publishBadgeCollection(client, user.id, next);
        void refreshBenefitQueries(client, badgeKey(user.id));
      });
    } catch (e) {
      if (current()) setError(t('badges.changeFailed'));
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const mark = d?.sponsorshipMark ?? sponsorshipMarkPreview,
    priority = own && mark.isSupporter && !d?.preview,
    stage = mark.currentStage ? localizeSponsorshipStage(mark.currentStage, locale) : null,
    months = mark.currentActiveMonths ?? mark.currentPaidMonths,
    nextStage = mark.stages.find((s) => !s.earned),
    progress =
      months != null
        ? nextStage
          ? Math.max(0, Math.min(100, (months / nextStage.paidMonths) * 100))
          : mark.isSupporter
            ? 100
            : 0
        : 0;
  const status = t(
      mark.partnerActive
        ? 'badges.supporter.partner'
        : mark.isActive
          ? 'badges.supporter.active'
          : mark.isSupporter
            ? 'badges.supporter.record'
            : 'badges.supporter.exclusive',
    ),
    label = mark.isSupporter
      ? (stage?.name ?? t('badges.supporter.default'))
      : t('badges.supporter.start'),
    selected = d?.profileMarkType === 'SPONSORSHIP',
    canSupport = own && mark.isSupporter && !d?.preview;
  const supporter = (
    <section className="pp-badge-category pp-badge-support">
      <div>
        <h2>{t('badges.supporter.title')}</h2>
        <p>{t('badges.supporter.subtitle')}</p>
      </div>
      <div className={`pp-badge-support-card${selected ? ' selected' : ''}`}>
        <SupportMark mark={mark} level={mark.currentStage?.level ?? 0} muted={!mark.isSupporter} />
        <div className="pp-badge-support-copy">
          <strong>
            {label} <small className="pp-badge-exclusive">SUPPORTER</small>
          </strong>
          <p>{t('badges.supporter.growth')}</p>
          <Link to="/sponsorship/benefits">
            {status}
            <FiChevronRight size={18} />
          </Link>
          {months != null && (
            <div className="pp-badge-progress">
              <i style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
        {canSupport ? (
          <button
            disabled={busy}
            className="pp-badge-support-select"
            aria-label={t(
              selected
                ? 'badges.supporter.removeAccessibility'
                : 'badges.supporter.setAccessibility',
            )}
            aria-pressed={selected}
            onClick={() => select(selected ? 'NONE' : 'SPONSORSHIP')}
          >
            <FiCheck size={14} />
          </button>
        ) : selected ? (
          <span className="pp-badge-check">
            <FiCheck size={12} />
          </span>
        ) : null}
      </div>
    </section>
  );
  return (
    <PageLayout paddingTop={0}>
      <div ref={root} className="pp-badges pp-badge-page" style={style}>
        <header>
          <button
            aria-label={t('common.back')}
            onClick={() =>
              window.history.state?.idx > 0
                ? navigate(-1)
                : navigate(nickname ? `/users/${encodeURIComponent(nickname)}` : '/mypage')
            }
          >
            <FiChevronLeft size={23} />
          </button>
          <h1>
            {locale === 'en'
              ? `${nickname ?? user?.nickname ?? ''}${nickname || user?.nickname ? '’s ' : ''}${t('badges.title')}`
              : `${nickname ?? user?.nickname ?? ''}${nickname || user?.nickname ? '님의 ' : ''}${t('badges.title')}`}
          </h1>
        </header>
        {!own && !nickname ? (
          <p>
            {locale === 'en'
              ? 'Sign in to view your badges.'
              : '활동 배지를 보려면 로그인해주세요.'}{' '}
            <Link to="/login?redirectUrl=%2Fmypage%2Fbadges">
              {locale === 'en' ? 'Sign in' : '로그인'}
            </Link>
          </p>
        ) : query.isPending ? (
          <BadgeSkeleton rows={4} />
        ) : query.isError ? (
          <BadgeError onRetry={() => query.refetch()} />
        ) : d ? (
          <>
            <div className="pp-badge-summary">
              <div>
                <strong>
                  {t(nickname ? 'badges.memberCollection' : 'badges.myCollection', { nickname })}
                </strong>
                <p>{t('badges.collectionDescription')}</p>
              </div>
              <span>
                {d.earnedCount}
                <small> / {d.totalCount}</small>
              </span>
            </div>
            {d.preview && <p className="pp-badge-notice">{t('badges.preview')}</p>}
            {error && <p role="alert">{error}</p>}
            {priority && supporter}
            {d.categories
              .filter((c) => c.badges.length)
              .map((category) => {
                const localized = localizeBadgeCategory(category, locale);
                return (
                  <section key={category.key} className="pp-badge-category">
                    <div>
                      <h2>{localized.name}</h2>
                      <p>{localized.description}</p>
                    </div>
                    <div className="pp-badge-grid">
                      {category.badges.map((b) => {
                        const unlocked = !!b.unlockedAt,
                          shown = unlocked,
                          l = localizeBadge(b, locale),
                          name = b.hidden && !shown ? t('badges.hidden') : l.name,
                          canSelect = own && unlocked && !d.preview;
                        return (
                          <button
                            key={b.key}
                            data-badge-key={b.key}
                            className={`pp-badge-card${!shown ? ' locked' : ''}${b.featured ? ' selected' : ''}${visible.includes(b.key) ? ' celebrating' : ''}`}
                            disabled={!canSelect || busy}
                            aria-pressed={b.featured}
                            aria-label={`${name}. ${unlocked ? t('badges.earnedAccessibility') : `${b.currentValue}/${b.targetValue}`}`}
                            title={
                              canSelect
                                ? t(
                                    b.featured
                                      ? 'badges.removeFeaturedHint'
                                      : 'badges.setFeaturedHint',
                                  )
                                : undefined
                            }
                            onClick={() => select(b.featured ? 'NONE' : 'ACTIVITY', b)}
                          >
                            <span className="pp-badge-card-top">
                              <BadgeIcon badge={b} muted={!shown} />
                              {b.featured && (
                                <span className="pp-badge-check">
                                  <FiCheck size={12} />
                                </span>
                              )}
                            </span>
                            <span className="pp-badge-card-copy">
                              <strong>{name}</strong>
                              <small>
                                {b.hidden && !shown ? t('badges.hiddenCondition') : l.description}
                              </small>
                            </span>
                            <span
                              className={`pp-badge-progress-copy${shown ? ' pp-badge-accent' : ''}`}
                            >
                              {unlocked
                                ? t(b.featured ? 'badges.featured' : 'badges.earned')
                                : `${b.currentValue} / ${b.targetValue}`}
                            </span>
                            <span className="pp-badge-progress">
                              <i
                                style={{
                                  width: `${shown ? 100 : Math.max(0, Math.min(100, b.progressPercent))}%`,
                                }}
                              />
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            {!priority && supporter}
          </>
        ) : null}
      </div>
    </PageLayout>
  );
}
